import { AxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex'
import React from 'react'
import useSWR from 'swr'
import { FetcherType, RolodexFileRef } from 'types'

import { RolodexFileLSQueryBody } from './useRolodexFileList'

/**
 * Returns a fetcher function for listing files that can be called inside callbacks.
 * Use this when you need to list files in event handlers or async functions.
 */
export function useGetRolodexFileLSFetcher(): (
  query: RolodexFileLSQueryBody
) => Promise<RolodexFileRef[]> {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  return React.useCallback(
    (query) => {
      return new Promise((resolve, reject) => {
        if (!isAuthenticated) {
          reject(new Error('User is not authenticated'))
          return
        }
        const body = JSON.stringify(query)
        return axios
          .post(`${apiURL}/api/v2/files/ls`, body, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err))
      })
    },
    [apiURL, axios, isAuthenticated]
  )
}

export const useRolodexFileLSBulk = (
  queries: ReadonlyArray<RolodexFileLSQueryBody> | undefined
): {
  /** An array of Rolodex file refs, useful for later loading */
  fileRefs: ReadonlyArray<RolodexFileRef>
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** To account for the initial case before the query fires  */
  hasEverLoaded: boolean
  /** Any errors returned from SWR */
  error: AxiosError
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
} => {
  //----------------------------------------------------------------------------

  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  const queryBodies = React.useMemo(() => {
    if (!queries || !isAuthenticated) return []
    return queries.map((query) => JSON.stringify(query))
  }, [queries, isAuthenticated])

  const fetcher = React.useCallback(
    (keys: ReadonlyArray<string>) =>
      Promise.all(
        keys.map(
          async (body) =>
            await axios
              .post(`${apiURL}/api/v2/files/ls`, body, {
                headers: { 'Content-Type': 'application/json' },
              })
              .then((res) => res.data)
        )
      ),
    [apiURL, axios]
  ) as FetcherType<RolodexFileRef[][]>

  const { data, isLoading, isValidating, error, mutate } = useSWR(
    queryBodies.length > 0 ? queryBodies : null,
    fetcher
  )

  const fileRefs = React.useMemo(() => (data ? data.flat() : []), [data])

  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return {
    fileRefs,
    isLoading,
    isValidating,
    error,
    hasEverLoaded: !!data,
    forceRefresh,
  }
}
